import { runExpert } from "./experts.js";
import { log, pii } from "./log.js";
import { Collection } from "./ports.js";
import { chosenRoute } from "./router/clarify.js";
import { ChatIntent } from "./router/intent.js";
import { Expert } from "./router/keyword.js";
import { intentIndex } from "./router/meaning-route.js";
import { routeChat } from "./router/route-chat.js";
/** The agent's experts by the intent that reaches them; any other intent the agent is handed is chat. */
const EXPERT = {
    [ChatIntent.remember]: Expert.remember,
    [ChatIntent.askSelf]: Expert.askSelf,
    [ChatIntent.askContact]: Expert.askContact,
    [ChatIntent.setRule]: Expert.setRule,
    [ChatIntent.find]: Expert.find,
};
/** What an expert takes from a route: a note is kept as the owner's own words, anything else as said. */
function expertRoute(text, { intent, slots }) {
    const expert = EXPERT[intent] ?? Expert.chat;
    const words = expert === Expert.remember ? (slots.note ?? text) : text;
    return { expert, text: words, ...(slots.contact ? { contact: slots.contact } : {}) };
}
/**
 * One message in, one route, and for the agent's own experts (notes, questions, contacts, finding, chat) one card out.
 * Routing is `routeChat` alone (ADR 0040, ADR 0042): the phone's language model when it has one, then its embedding
 * model, the word lists otherwise.
 */
export class Agent {
    ports;
    /** The index over the intent sayings, embedded once for this agent's life; none on a device without an embedder. */
    meaning;
    constructor(ports) {
        this.ports = ports;
        this.meaning = ports.embedder ? intentIndex(ports.embedder) : undefined;
    }
    /** The message's route, with this owner's contacts as the names a route may carry. */
    async route(text, ctx) {
        const contacts = await this.ports.storage.list(Collection.contacts);
        const full = { ...ctx, contacts: contacts.map((c) => c.name) };
        return routeChat(text, full, this.ports.model, { meaning: this.meaning });
    }
    /** The route for an intent the owner picked from the agent's question, read from the words first asked. */
    async chosen(intent, text, ctx) {
        const contacts = await this.ports.storage.list(Collection.contacts);
        return chosenRoute(intent, text, { ...ctx, contacts: contacts.map((c) => c.name) });
    }
    /** The card for a routed message, from the expert its intent names; a free reply reads the chat so far. */
    async answer(text, chat, recent) {
        const start = Date.now();
        const contacts = await this.ports.storage.list(Collection.contacts);
        const route = expertRoute(text, chat);
        log.debug("chat: expert", { expert: route.expert, contact: pii(route.contact), text: pii(route.text) });
        const card = await runExpert(route, this.ports, contacts, recent);
        log.debug("chat: reply", { kind: card.kind, card: pii(JSON.stringify(card)) });
        // What happened, never what was said: the expert, the outcome, the size of the message and the time taken.
        log.info("chat handled", { expert: route.expert, card: card.kind, chars: text.length, ms: Date.now() - start });
        return card;
    }
}
//# sourceMappingURL=agent.js.map